import { useEffect, useState, useRef } from 'react'
import { Text, useText } from '@visx/text'
import { FormState } from '../slices/formSlice'
import { useAppSelector } from '../hooks'
import { selectInitialized, selectMediaItem, selectFonts, toggleBackgroundImage } from '../slices/mediaSlice'
import './RenderImageSec.css'


interface RenderImage2ElementProps {
    width?: number
    height?: number
    svgRef?: React.Ref<SVGSVGElement>
}

export type RenderImage2Props = RenderImage2ElementProps & Omit<FormState, 'quote' | 'email'>

function RenderImage2({
    width,
    height = width,
    svgRef,
    image,
    statement: quoteOriginal,
    name,
    age,
    origin,
}: RenderImage2Props) {
    const viewboxWidth = 1080
    const viewboxHeight = viewboxWidth
    const [imageHeight, setImageHeight] = useState<number>(0)
    const [imageWidth, setImageWidth] = useState<number>(0)
    const margin = {
        top: 0.03 * viewboxHeight,
        imageTop: 0.222 * viewboxHeight,
        left: 0.25 * viewboxWidth,
        right: 0.25 * viewboxWidth,
        between_x: 0.034 * viewboxWidth,
        between_y: 0.13 * viewboxHeight,
        bottom: 100,
        quote_width: 150, // Width of a maximum of 9em
        subline_width: 110,
    }

    const isWithLogo = useAppSelector((state) => state.media.media.hasOwnProperty('layout_bottom_withLogo'))
    const backgroundImageKey = isWithLogo ? 'layout_bottom_withLogo' : 'layout_bottom_withoutLogo'
    const backgroundImage = useAppSelector(selectMediaItem(backgroundImageKey))
    const fonts = useAppSelector(selectFonts)
    const initialized = useAppSelector(selectInitialized)

    const callForAction = {
        width: 400,
        height: 210,
    }
    const hashtagDimensions = {
        width: 1200 / 6,
        height: 199 / 6,
    }
    const quoteTextBox = useRef<SVGTextElement>(null)
    const personTextBox = useRef<SVGTextElement>(null)
    const [personYPosition, setPersonYPosition] = useState(viewboxHeight - margin.bottom)
    const maxImageWidth = 950
    const maxImageHeight = 600

    const subline = [name, age ? `${age} Jahre` : false, origin].filter((e) => e).join(', ')
    const quoteWithoutDoubleWhitespace = quoteOriginal?.replace(/\s+/g, ' ') ?? ''
    const { wordsByLines } = useText({
        fontSize: `${0.05 * viewboxWidth}pt`,
        className: 'quote',
        y: 0.125 * viewboxHeight,
        x: 0.966 * viewboxWidth,
        width: margin.quote_width,
        textAnchor: 'start',
        verticalAnchor: 'start',
        children: quoteWithoutDoubleWhitespace,
    })
    const quoteTruncated =
        wordsByLines.length <= 9
            ? quoteWithoutDoubleWhitespace
            : quoteWithoutDoubleWhitespace.substring(
                  0,
                  quoteWithoutDoubleWhitespace.indexOf(wordsByLines[9].words.join(' ')),
              ) + '...'
    useEffect(() => {
        if (image) {
            const img = new Image()
            img.src = image
            img.onload = () => {
                const scaledWidth = Math.min(img.width, maxImageWidth)
                const scaledHeight = Math.min(img.height, maxImageHeight)
                setImageWidth(scaledWidth)
                setImageHeight(scaledHeight)
            }
        }
    }, [image])

    useEffect(() => {
        setPersonYPosition(
            Math.min(
                quoteTextBox.current
                    ? margin.top + quoteTextBox.current.getBBox().height + margin.between_y
                    : viewboxHeight - margin.bottom,
                viewboxHeight - callForAction.height - hashtagDimensions.height - 35,
            ),
        )
    }, [
        quoteTextBox,
        viewboxHeight,
        quoteTruncated,
        name,
        age,
        origin,
        margin.between_x,
        margin.between_y,
        margin.bottom,
        margin.imageTop,
        margin.top,
        callForAction.width,
        callForAction.height,
        hashtagDimensions.height,
    ])

    if (!initialized) {
        return <></>
    }

    return (
        <svg viewBox={`0 0 ${viewboxWidth} ${viewboxHeight}`} width={width} height={height} ref={svgRef}>
            <style
                dangerouslySetInnerHTML={{
                    __html: `
@font-face {
    font-family: 'Mont Book';
    src: url('${fonts.montbook.woff2}') format('woff2'), url('${fonts.montbook.woff}') format('woff');
}
@font-face {
    font-family: 'Mont Heavy';
    src: url('${fonts.montheavy.woff2}') format('woff2'), url('${fonts.montheavy.woff}') format('woff');
}
@font-face {
    font-family: 'Waiting for the Sunrise';
    src: url('${fonts.waitingforthesunrise.woff2}') format('woff2'), url('${fonts.waitingforthesunrise.woff}') format('woff');
}
.quote {
    fill: #FFFFFF;
    font-family: 'Waiting for the Sunrise';
    text-align: right;
    text-transform: none;
    
}
.person {
    fill: #FFFFFF;
    font-family: 'Waiting for the Sunrise';
}
`,
                }}
            />
            
            {backgroundImage && <image href={backgroundImage} height={viewboxHeight} width={viewboxWidth} />}
            <Text
                fontSize={`${0.04 * viewboxWidth}pt`}
                className="quote"
                y={0.40 * viewboxHeight}
                x={0.14 * viewboxWidth}
                width={margin.quote_width}
                textAnchor="start"
                verticalAnchor="start"
                innerTextRef={quoteTextBox}
                fill="#FFFFFF"
                fontFamily="Waiting for the Sunrise"
            >
                {quoteTruncated}
            </Text>
            {subline && (
                <Text
                    fontSize={`${0.038 * viewboxWidth}pt`}
                    className="person"
                    y={820}
                    x={950}
                    width={margin.subline_width}
                    textAnchor="end"
                    verticalAnchor="start"
                    innerTextRef={personTextBox}
                    fill="#FFFFFF"
                    fontFamily="Waiting for the Sunrise"
                    transform={`rotate(-5 ${viewboxWidth - 200} ${personYPosition + 250})`}
                >
                    {'Geht wählen! ' + subline}
                </Text>
            )}
        </svg>
    )
}

export default RenderImage2
